import { useRef, useMemo, useCallback, useEffect, useState } from 'react'
import { Box, Spinner } from '@chakra-ui/react';
import Map, { Source, Layer, Popup } from 'react-map-gl/maplibre'
import type { MapRef, MapLayerMouseEvent } from 'react-map-gl/maplibre'
import type { Map as MapLibreMap, LngLatBoundsLike } from 'maplibre-gl'
import { getMapStyle, DEFAULT_VIEW_STATE, MAP_LABEL_FONT } from '../../lib/mapConfig'
import { SCORING_PROFILE_COLORS } from '../../types/analyticsMap'
import type { EnrichedContactLocation, BoundingBox, HighValueVoter } from '../../types/analyticsMap'
import { flagLabel } from '../../types/analyticsFlags'
import { LightSurface } from '../shared'
import { formatGeoUnitLabel } from './geoUnitLabel'
import type { GeoUnitTypeKey } from '../../types/geoAnalytics'
import 'maplibre-gl/dist/maplibre-gl.css'

interface AnalyticsMapViewProps {
  contacts: EnrichedContactLocation[] | undefined
  isLoading: boolean
  onMapMove: (bbox: BoundingBox) => void
  onContactClick: (contactId: string) => void
  onMapReady?: (map: MapLibreMap) => void
  highValueVoters?: HighValueVoter[]
  dimNonHighlighted?: boolean
  flyToBounds?: LngLatBoundsLike | null
  choroplethData?: GeoJSON.FeatureCollection | null
  choroplethFillColor?: unknown[] | null
  choroplethOpacity?: number
  onUnitClick?: (unitId: string) => void
  swingData?: GeoJSON.FeatureCollection | null
  /** Active geography — used to compose a disambiguating hover label for census tract/block-group units. */
  geoUnitType?: GeoUnitTypeKey
  /** When false, contact dots/clusters are not rendered (Contacts layer off). Default true. */
  showContacts?: boolean
}

const CONTACTS_CLUSTER_ID = 'analytics-contact-clusters'
const CONTACTS_CLUSTER_COUNT_ID = 'analytics-contact-cluster-count'
const CONTACTS_UNCLUSTERED_ID = 'analytics-contact-dots'
const HIGH_VALUE_LAYER_ID = 'analytics-high-value-dots'
const CONTACTS_SOURCE_ID = 'analytics-contacts-source'
export const CHOROPLETH_FILL_ID = 'geo-choropleth-fill'
export const CHOROPLETH_HATCH_ID = 'geo-choropleth-hatch'
export const CHOROPLETH_LINE_ID = 'geo-choropleth-line'
export const SWING_LAYER_ID = 'geo-swing-arrows'
const HATCH_IMAGE_ID = 'suppressed-hatch'

function highValueToGeoJson(voters: HighValueVoter[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: voters.map(v => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [v.lng, v.lat],
      },
      properties: {
        id: v.contactId,
        name: v.name,
        score: v.priorityScore,
        profileName: v.profileName,
        flagsText: (v.flagKeys ?? []).map(flagLabel).join(', '),
      },
    })),
  }
}

/** Build a MapLibre match expression for data-driven circle colors by profileName */
function buildProfileColorExpression(): (string | string[])[] {
  const expr: (string | string[])[] = ['match', ['get', 'profileName']]
  for (const [name, color] of Object.entries(SCORING_PROFILE_COLORS)) {
    expr.push(name, color)
  }
  expr.push('#F59E0B') // fallback
  return expr
}

export function contactsToGeoJson(contacts: EnrichedContactLocation[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: contacts.map(c => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [c.longitude, c.latitude],
      },
      properties: {
        id: c.id,
        name: `${c.firstName} ${c.lastName}`,
        address: c.streetAddress,
        party: c.party ?? '',
      },
    })),
  }
}

/** Stable empty collection so a hidden contacts layer keeps the same reference across renders. */
const EMPTY_FEATURE_COLLECTION: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

/**
 * Data for the ALWAYS-mounted contacts source. Returns an empty collection (never null) when the
 * layer is hidden so the `<Source>` and its cluster/dot layers stay mounted.
 *
 * DT-BUG-2: previously the source was conditionally unmounted on toggle-off, and react-map-gl's
 * source teardown can call `removeSource` while a child layer still references it —
 * "Source … cannot be removed while layer … is using it" — which leaves the map's reconciliation
 * half-torn-down and stops the choropleth from rendering any metric until a full reload. Keeping the
 * source mounted and swapping its data avoids the teardown entirely.
 */
export function contactsSourceData(
  showContacts: boolean,
  contacts: EnrichedContactLocation[] | undefined,
): GeoJSON.FeatureCollection {
  return showContacts && contacts ? contactsToGeoJson(contacts) : EMPTY_FEATURE_COLLECTION
}

const PROFILE_COLOR_EXPR = buildProfileColorExpression()

/**
 * Which choropleth sub-layers to render.
 *
 * DT-BUG-3: the boundary LINE draws whenever there's geometry (a geography chosen), so a chosen
 * geography shows unfilled boundaries even with NO metric selected. The fill + suppressed-hatch
 * only draw once a metric supplies a fill color. Previously the whole `<Source>` was gated on
 * `choroplethData && choroplethFillColor`, so picking a geography without a metric rendered nothing.
 */
export function choroplethLayerVisibility(
  data: GeoJSON.FeatureCollection | null | undefined,
  fillColor: unknown[] | null | undefined,
): { source: boolean; fill: boolean; line: boolean } {
  const source = !!data
  return { source, fill: source && !!fillColor, line: source }
}

export function AnalyticsMapView({
  contacts,
  isLoading,
  onMapMove,
  onContactClick,
  onMapReady,
  highValueVoters,
  dimNonHighlighted = false,
  flyToBounds,
  choroplethData,
  choroplethFillColor,
  choroplethOpacity,
  onUnitClick,
  swingData,
  geoUnitType,
  showContacts = true,
}: AnalyticsMapViewProps) {
  const mapRef = useRef<MapRef>(null)
  const moveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  // Fly to bounds on a new selection (list or area-unit load). Also keyed on
  // mapLoaded so bounds that resolve *before* the map's load event still snap the
  // camera once it's ready (otherwise the first selection silently no-ops).
  useEffect(() => {
    if (!flyToBounds || !mapLoaded || !mapRef.current) return
    mapRef.current.fitBounds(flyToBounds, {
      padding: 60,
      maxZoom: 15,
      duration: 1200,
    })
  }, [flyToBounds, mapLoaded])

  // Always a FeatureCollection (empty when hidden) so the contacts <Source> stays mounted — see
  // contactsSourceData / DT-BUG-2.
  const contactsGeoJson = useMemo(
    () => contactsSourceData(showContacts, contacts),
    [showContacts, contacts]
  )

  const highValueGeoJson = useMemo(
    () => (highValueVoters && highValueVoters.length > 0 ? highValueToGeoJson(highValueVoters) : null),
    [highValueVoters]
  )

  const contactDotOpacity = dimNonHighlighted && highValueGeoJson ? 0.12 : 0.8

  const handleMoveEnd = useCallback(() => {
    if (moveTimeoutRef.current) clearTimeout(moveTimeoutRef.current)
    moveTimeoutRef.current = setTimeout(() => {
      const map = mapRef.current
      if (!map) return
      const bounds = map.getBounds()
      onMapMove({
        minLng: bounds.getWest(),
        minLat: bounds.getSouth(),
        maxLng: bounds.getEast(),
        maxLat: bounds.getNorth(),
      })
    }, 300)
  }, [onMapMove])

  const [hoverInfo, setHoverInfo] = useState<{ longitude: number; latitude: number; name: string; displayValue: string } | null>(null)

  const handleClick = useCallback(
    (e: MapLayerMouseEvent) => {
      // Swing arrows are hover-only; swallow their clicks so they don't misfire onContactClick.
      if (e.features?.some(f => f.layer.id === SWING_LAYER_ID)) return

      // Cluster bubble → expand to the next zoom that breaks it apart.
      const cluster = e.features?.find(f => f.layer.id === CONTACTS_CLUSTER_ID)
      if (cluster?.properties) {
        const clusterId = cluster.properties.cluster_id
        const src = mapRef.current?.getMap().getSource(CONTACTS_SOURCE_ID)
        if (src && 'getClusterExpansionZoom' in src) {
          ;(src as unknown as {
            getClusterExpansionZoom: (id: number, cb: (err: unknown, zoom: number) => void) => void
          }).getClusterExpansionZoom(clusterId as number, (err, zoom) => {
            if (err) return
            const geom = cluster.geometry as GeoJSON.Point
            mapRef.current?.easeTo({ center: [geom.coordinates[0], geom.coordinates[1]], zoom })
          })
        }
        return
      }

      // Individual contact / high-value dot.
      const feature = e.features?.[0]
      if (feature?.properties?.id && feature.layer.id !== CHOROPLETH_FILL_ID) {
        onContactClick(feature.properties.id as string)
        return
      }
      const choroplethFeature = e.features?.find(f => f.layer.id === CHOROPLETH_FILL_ID)
      if (choroplethFeature?.properties?.id && onUnitClick) {
        onUnitClick(choroplethFeature.properties.id as string)
        return
      }
    },
    [onContactClick, onUnitClick]
  )

  const handleMouseMove = useCallback((e: MapLayerMouseEvent) => {
    const swingFeature = e.features?.find(f => f.layer.id === SWING_LAYER_ID)
    if (swingFeature?.properties) {
      setHoverInfo({
        longitude: e.lngLat.lng,
        latitude: e.lngLat.lat,
        // displayValue already embeds the unit name — pass '' so the tooltip doesn't render it twice.
        name: '',
        displayValue: (swingFeature.properties.displayValue as string) ?? '',
      })
      return
    }
    const hvFeature = e.features?.find(f => f.layer.id === HIGH_VALUE_LAYER_ID)
    if (hvFeature?.properties) {
      const p = hvFeature.properties as { name?: string; score?: number; profileName?: string; flagsText?: string }
      const scorePart = `${Math.round(p.score ?? 0)} (${p.profileName ?? ''})`
      setHoverInfo({
        longitude: e.lngLat.lng,
        latitude: e.lngLat.lat,
        name: p.name ?? '',
        displayValue: p.flagsText ? `${scorePart} · ${p.flagsText}` : scorePart,
      })
      return
    }
    const feature = e.features?.find(f => f.layer.id === CHOROPLETH_FILL_ID)
    if (feature?.properties) {
      setHoverInfo({
        longitude: e.lngLat.lng,
        latitude: e.lngLat.lat,
        name: formatGeoUnitLabel(
          geoUnitType,
          feature.properties.code as string | undefined,
          feature.properties.name as string | undefined,
        ),
        displayValue: (feature.properties.displayValue as string) ?? '',
      })
    } else {
      setHoverInfo(null)
    }
  }, [geoUnitType])

  const handleMapLoad = useCallback(() => {
    setMapLoaded(true)
    const map = mapRef.current
    if (!map) return
    const bounds = map.getBounds()
    onMapMove({
      minLng: bounds.getWest(),
      minLat: bounds.getSouth(),
      maxLng: bounds.getEast(),
      maxLat: bounds.getNorth(),
    })
    const rawMap = (map as unknown as { getMap?: () => MapLibreMap }).getMap?.()
    if (onMapReady && rawMap) onMapReady(rawMap)
    if (rawMap && !rawMap.hasImage(HATCH_IMAGE_ID)) {
      const size = 8
      const data = new Uint8ClampedArray(size * size * 4)
      for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
          const idx = (y * size + x) * 4
          if ((x + y) % 8 < 2) {
            data[idx] = 148; data[idx + 1] = 163; data[idx + 2] = 184; data[idx + 3] = 255
          }
        }
      }
      rawMap.addImage(HATCH_IMAGE_ID, { width: size, height: size, data })
    }
    const registerArrow = (id: string, color: string) => {
      if (!rawMap || rawMap.hasImage(id)) return
      const size = 32
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')!
      ctx.strokeStyle = color
      ctx.fillStyle = color
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      // shaft
      ctx.beginPath()
      ctx.moveTo(size / 2, size - 4)
      ctx.lineTo(size / 2, 8)
      ctx.stroke()
      // head
      ctx.beginPath()
      ctx.moveTo(size / 2, 2)
      ctx.lineTo(size / 2 - 7, 12)
      ctx.lineTo(size / 2 + 7, 12)
      ctx.closePath()
      ctx.fill()
      const imageData = ctx.getImageData(0, 0, size, size)
      rawMap.addImage(id, { width: size, height: size, data: new Uint8ClampedArray(imageData.data) })
    }
    registerArrow('swing-arrow-d', '#2563EB')
    registerArrow('swing-arrow-r', '#DC2626')
  }, [onMapMove, onMapReady])

  // Contacts source/layers are always mounted (empty when hidden), so the cluster layer is a stable
  // anchor: keep the choropleth beneath it whether or not contacts are currently shown.
  const choroplethBeforeId = CONTACTS_CLUSTER_ID

  // Boundary line draws with geometry alone; fill/hatch wait on a metric fill color (DT-BUG-3).
  const choroplethLayers = choroplethLayerVisibility(choroplethData, choroplethFillColor)

  return (
    <Box position="relative" w="100%" h="100%">
      {isLoading && (
        <LightSurface>
          <Box
            position="absolute"
            top={3}
            right={3}
            zIndex={10}
            bg="whiteAlpha.800"
            px={3}
            py={1}
            borderRadius="md"
            shadow="sm"
          >
            <Spinner size="sm" color="accent" />
          </Box>
        </LightSurface>
      )}
      <Map
        ref={mapRef}
        initialViewState={DEFAULT_VIEW_STATE}
        style={{ width: '100%', height: '100%' }}
        mapStyle={getMapStyle()}
        onMoveEnd={handleMoveEnd}
        onLoad={handleMapLoad}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverInfo(null)}
        interactiveLayerIds={[CONTACTS_CLUSTER_ID, CONTACTS_UNCLUSTERED_ID, HIGH_VALUE_LAYER_ID, SWING_LAYER_ID, CHOROPLETH_FILL_ID]}
        cursor="pointer"
      >
        {/* Choropleth layer — rendered under contact/high-value overlays.
            The boundary LINE draws whenever geometry exists, so a chosen geography shows unfilled
            boundaries even with NO metric selected (DT-BUG-3); fill + suppressed-hatch only draw
            once a metric supplies a fill color. See choroplethLayerVisibility. */}
        {choroplethLayers.source && (
          <Source id="geo-choropleth-source" type="geojson" data={choroplethData as GeoJSON.FeatureCollection}>
            {/* Each Layer must be a DIRECT child of <Source> — never grouped in a fragment.
                react-map-gl injects the source id via React.Children.map/cloneElement, which does
                not flatten fragments, so a wrapped Layer never receives `source` and maplibre
                rejects it ("missing required property source"). See mapSourceChildren.guard.test. */}
            {choroplethLayers.fill && (
              <Layer
                id={CHOROPLETH_FILL_ID}
                type="fill"
                beforeId={choroplethBeforeId}
                paint={{
                  'fill-color': choroplethFillColor as any,
                  'fill-opacity': choroplethOpacity ?? 0.7,
                }}
              />
            )}
            {choroplethLayers.fill && (
              <Layer
                id={CHOROPLETH_HATCH_ID}
                type="fill"
                beforeId={choroplethBeforeId}
                filter={['to-boolean', ['get', 'suppressed']] as any}
                paint={{ 'fill-pattern': HATCH_IMAGE_ID, 'fill-opacity': choroplethOpacity ?? 0.7 }}
              />
            )}
            <Layer
              id={CHOROPLETH_LINE_ID}
              type="line"
              beforeId={choroplethBeforeId}
              paint={{ 'line-color': '#94A3B8', 'line-width': 0.5 }}
            />
          </Source>
        )}

        {/* Swing arrow layer — rendered on top of choropleth */}
        {swingData && (
          <Source id="geo-swing-source" type="geojson" data={swingData}>
            <Layer
              id={SWING_LAYER_ID}
              type="symbol"
              layout={{
                'icon-image': ['match', ['get', 'direction'], 'D', 'swing-arrow-d', 'swing-arrow-r'] as any,
                'icon-rotate': ['get', 'rotation'] as any,
                'icon-size': ['match', ['get', 'sizeBin'], 0, 0.45, 1, 0.65, 2, 0.85, 1.1] as any,
                'icon-allow-overlap': true,
                'icon-rotation-alignment': 'map',
              }}
            />
          </Source>
        )}

        {/* Contact dots — clustered (bubbles at wide zoom → individual dots on zoom-in).
            The source is ALWAYS mounted (contactsSourceData feeds empty data when hidden) — do NOT
            make this conditional on "contacts shown", or toggling it off tears the source down while
            its layers still reference it and corrupts the choropleth (DT-BUG-2). */}
        {contactsGeoJson && (
          <Source
            id={CONTACTS_SOURCE_ID}
            type="geojson"
            data={contactsGeoJson}
            cluster
            clusterMaxZoom={14}
            clusterRadius={50}
          >
            <Layer
              id={CONTACTS_CLUSTER_ID}
              type="circle"
              filter={['has', 'point_count']}
              paint={{
                'circle-color': '#3B82F6',
                'circle-radius': ['step', ['get', 'point_count'], 14, 10, 18, 50, 24, 100, 30],
                'circle-opacity': contactDotOpacity,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
              }}
            />
            <Layer
              id={CONTACTS_CLUSTER_COUNT_ID}
              type="symbol"
              filter={['has', 'point_count']}
              layout={{
                'text-field': '{point_count_abbreviated}',
                'text-font': MAP_LABEL_FONT,
                'text-size': 12,
              }}
              paint={{ 'text-color': '#ffffff' }}
            />
            <Layer
              id={CONTACTS_UNCLUSTERED_ID}
              type="circle"
              filter={['!', ['has', 'point_count']]}
              paint={{
                'circle-radius': 4,
                'circle-color': '#3B82F6',
                'circle-opacity': contactDotOpacity,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-opacity': contactDotOpacity,
              }}
            />
          </Source>
        )}

        {/* High-value voter markers — data-driven colors by profile */}
        {highValueGeoJson && (
          <Source id="analytics-high-value-source" type="geojson" data={highValueGeoJson}>
            <Layer
              id={HIGH_VALUE_LAYER_ID}
              type="circle"
              paint={{
                'circle-radius': 7,
                'circle-color': PROFILE_COLOR_EXPR as any,
                'circle-opacity': 0.9,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
              }}
            />
          </Source>
        )}

        {hoverInfo && (
          <Popup
            longitude={hoverInfo.longitude}
            latitude={hoverInfo.latitude}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            offset={8}
          >
            {/* The maplibre popup box is always white (its own CSS, not Chakra), so in
                dark mode the content must force LIGHT tokens or the text inherits the
                light dark-canvas ink and vanishes on the white box. §5.6 map-island rule. */}
            <LightSurface>
              <Box color="canvas.fg">
                {hoverInfo.name ? <><strong>{hoverInfo.name}</strong> — {hoverInfo.displayValue}</> : hoverInfo.displayValue}
              </Box>
            </LightSurface>
          </Popup>
        )}
      </Map>
    </Box>
  )
}
